/**
 * Error Constants (ESM)
 *
 * Enterprise error handling constants for the ParcelPuffin ecosystem.
 * These constants provide standardized error types, impact levels,
 * resolution statuses, and API error codes.
 *
 * @module @puffin-core/error-handling/constants/errorConstants
 */

/**
 * Error types enum
 * @enum {string}
 */
export const ErrorTypes = Object.freeze({
  SYSTEM: 'system',
  UI: 'ui',
  API: 'api',
  DATABASE: 'database',
  VALIDATION: 'validation',
  NETWORK: 'network',
  SECURITY: 'security',
  BUSINESS_RULE: 'business_rule',
  PERFORMANCE: 'performance',
  UNKNOWN: 'unknown',
});

/**
 * Error impact levels
 * @enum {string}
 */
export const ErrorImpactLevels = Object.freeze({
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
});

/**
 * Refuse a value outside a closed vocabulary, by name.
 *
 * An impact level or an error type is compared by VALUE downstream (`NotificationErrorLogger` ranks a level by its
 * index in `ErrorImpactLevels`), so a value outside the vocabulary does not fail: it ranks below every threshold and
 * the error is neither logged nor notified. Six pos-web contexts passed `'MEDIUM'` for the value `'medium'` and lost
 * every error that way (D1 errorCode unit 4). The vocabulary is closed: an unknown value is refused here, at the
 * boundary, with its name.
 *
 * @param {Readonly<Record<string, string>>} vocabulary - The frozen enum.
 * @param {string} label - The vocabulary's name, for the refusal.
 * @param {*} value - The candidate value.
 * @returns {string} The value, when it is a member.
 * @throws {TypeError} When the value is not a member.
 */
function assertMemberOf(vocabulary, label, value) {
  const members = Object.values(vocabulary);
  if (!members.includes(value)) {
    throw new TypeError(
      `Unknown ${label} ${JSON.stringify(value)}; expected one of ${members.join(', ')}`,
    );
  }
  return value;
}

/**
 * Refuse an impact level outside {@link ErrorImpactLevels}, by name.
 *
 * @param {*} value - The candidate impact level.
 * @returns {string} The value, when it is a member.
 * @throws {TypeError} When the value is not a member.
 */
export function assertErrorImpactLevel(value) {
  return assertMemberOf(ErrorImpactLevels, 'error impact level', value);
}

/**
 * Refuse an error type outside {@link ErrorTypes}, by name.
 *
 * @param {*} value - The candidate error type.
 * @returns {string} The value, when it is a member.
 * @throws {TypeError} When the value is not a member.
 */
export function assertErrorType(value) {
  return assertMemberOf(ErrorTypes, 'error type', value);
}

/**
 * Error resolution status
 * @enum {string}
 */
export const ErrorResolutionStatus = Object.freeze({
  UNRESOLVED: 'unresolved',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  WONT_FIX: 'wont_fix',
  DEFERRED: 'deferred',
});

/**
 * API error codes
 * @constant
 * @type {Object}
 */
export const API_ERROR_CODES = Object.freeze({
  // Network errors
  NETWORK_ERROR: 'network_error',
  TIMEOUT: 'request_timeout',
  ABORTED: 'request_aborted',

  // Server errors
  SERVER_ERROR: 'server_error',
  SERVICE_UNAVAILABLE: 'service_unavailable',

  // Authentication errors
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  TOKEN_EXPIRED: 'token_expired',

  // Request errors
  BAD_REQUEST: 'bad_request',
  NOT_FOUND: 'not_found',
  VALIDATION_ERROR: 'validation_error',
  CONFLICT: 'conflict',

  // Response errors
  INVALID_RESPONSE: 'invalid_response',
  PARSE_ERROR: 'parse_error',

  // General errors
  UNKNOWN_ERROR: 'unknown_error',
});
